import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot, Slottable } from '@radix-ui/react-slot';
import { forwardRef } from 'react';
import { cn } from "../lib/cn.js";
const VARIANTS = {
    primary: 'bg-(--brand-primary) text-(--text-on-brand) hover:bg-(--brand-primary-hover) active:bg-(--brand-primary-active) shadow-(--shadow-sm)',
    secondary: 'bg-(--surface) text-(--text-primary) border border-(--border-default) hover:bg-(--surface-hover) active:bg-(--surface-active)',
    ghost: 'bg-transparent text-(--text-secondary) hover:bg-(--surface-hover) hover:text-(--text-primary) active:bg-(--surface-active)',
    danger: 'bg-(--danger) text-white hover:brightness-110 active:brightness-95 shadow-(--shadow-sm)',
    link: 'bg-transparent text-(--brand-primary) underline-offset-4 hover:underline p-0 h-auto',
};
const SIZES = {
    sm: 'h-(--control-height-sm) px-2.5 text-(length:--text-xs) gap-1.5',
    md: 'h-(--control-height-md) px-3 text-(length:--text-sm) gap-2',
    lg: 'h-(--control-height-lg) px-4 text-(length:--text-base) gap-2',
    icon: 'h-(--control-height-md) w-(--control-height-md) p-0',
};
export const Button = forwardRef(({ className, variant = 'secondary', size = 'md', asChild = false, loading = false, leadingIcon, trailingIcon, children, disabled, type = 'button', onClick, ...props }, ref) => {
    const Component = asChild ? Slot : 'button';
    return (_jsxs(Component, { ref: ref, type: asChild ? undefined : type, disabled: disabled ?? false, "aria-busy": loading || undefined, "data-loading": loading || undefined, onClick: (event) => {
            /*
             * Ocupado é ocupado também para o teclado — F248.
             *
             * O `pointer-events: none` do estado de carregando só segura o
             * MOUSE. O Enter num campo do formulário (o envio implícito clica o
             * botão de enviar) e o Enter/Espaço no botão focado passavam, e um
             * "Cadastrar" apertado duas vezes criava dois produtos. Cancelar o
             * clique aqui cancela o envio do formulário junto.
             */
            if (loading) {
                event.preventDefault();
                return;
            }
            onClick?.(event);
        }, className: cn(
        /*
         * `shrink-0` — F191.
         *
         * O dono, com a aba Variações aberta: *"olha esses botões que
         * quebrou o tamanho"*. Nas linhas de cor não-principal há TRÊS
         * botões de ícone na última coluna; nas outras, dois. Com três, a
         * coluna aperta, e como todo botão é filho de um `flex` com
         * `flex-shrink: 1` por padrão, os três encolhiam — junto com os
         * ícones de 16px dentro deles, que viravam manchinhas.
         *
         * Na raiz e não na tela porque não é defeito daquela tabela: vale
         * para todo botão de ícone em coluna de ações, e ninguém no código
         * contornava isso à mão — o que significa que o próximo lugar a
         * apertar quebraria igual, calado.
         *
         * `whitespace-nowrap` logo ao lado já dizia a intenção: este
         * componente não se deforma para caber. Faltava valer para a largura.
         */
        'inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-(--radius-md)', 'font-(--weight-medium) transition-colors duration-(--duration-fast) ease-(--ease-out)', 'disabled:pointer-events-none disabled:opacity-50', 'data-[loading]:pointer-events-none data-[loading]:opacity-70', VARIANTS[variant], SIZES[size], className), ...props, children: [leadingIcon, _jsx(Slottable, { children: children }), trailingIcon] }));
});
Button.displayName = 'Button';
//# sourceMappingURL=button.js.map